import React, {
  ComponentProps,
  forwardRef,
  FunctionComponent,
  MutableRefObject,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import ReactSelectAsync from 'react-select/async';
import debounce from 'es6-promise-debounce';
import Row from './Row';
import Fieldset from './Fieldset';
import { Badge } from './Tab';
import { components } from 'react-select';
import { InnerSelectModal } from '../SelectModal/SelectModal';
import { LuckinsPill } from '../StatusPill/StatusPill';
import { isShiftKeyHeldDown } from '../react-data-grid/utils/keyboardUtils';
import { Direction, IFilterInput } from '../../graphql/schema/p4types';
import { ReadOnlyComponent } from './DropdownReadOnlyComponent';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { makeStyles } from '@fluentui/react-components';

const Async = ReactSelectAsync as (
  props: ComponentProps<typeof ReactSelectAsync> & { onSearchIconClick: () => void }
) => ReactElement;

type IPageConfig = { first: number; after: number; orderBy: string; orderDirection: Direction };

export const useSearchVariables = (additionalFilters?: Array<IFilterInput>, pageConfig?: IPageConfig) => {
  const { first, after, orderBy, orderDirection } = pageConfig ?? {};

  const [genericFilter, setGenericFilter] = useState(null);

  return useCallback(
    (genericFilterString = genericFilter) => {
      if (genericFilterString !== genericFilter) {
        setGenericFilter(genericFilterString);
      }

      const genericFilters = [
        {
          filterOperation: 'CONTAINS',
          filterParameters: {
            field: 'generic',
            value: genericFilterString,
          },
        },
      ];

      return {
        first: first ?? 15,
        after: after ?? 0,
        orderBy: orderBy ?? 'id',
        orderDirection: orderDirection ?? Direction.Asc,
        filters: [...(additionalFilters ?? []), ...genericFilters],
      };
    },
    [additionalFilters, after, first, genericFilter, orderBy, orderDirection]
  );
};

export const MultiMenuList = (props) => {
  const { children, className, cx, getStyles, isMulti } = props;

  if (Array.isArray(children)) {
    return (
      <Row style={{ width: '100%', marginLeft: 0, marginRight: 0 }}>
        {children.map((group) => {
          return (
            <Fieldset key={group.key} cols={1 / children.length} style={{ paddingRight: 0 }}>
              <div
                // @ts-ignore
                css={getStyles('menuList', props)}
                className={cx(
                  {
                    'menu-list': true,
                    'menu-list--is-multi': isMulti,
                  },
                  className
                )}
                style={{ boxSizing: null }}
              >
                {group}
              </div>
            </Fieldset>
          );
        })}
      </Row>
    );
  }
  return children;
};

const SearchIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <span onMouseDown={props.selectProps.onSearchIconClick}>
        <i className="iconSelect">search</i>
      </span>
    </components.DropdownIndicator>
  );
};

const useStyles = makeStyles({
  groupLabel: {
    color: p4Tokens.colorNeutralForeground3,
  },
});

const MAX_MENU_HEIGHT = 400; // px

type IMultiQueryDropdown = {
  active: boolean;
  clearButton: boolean;
  mandatory: boolean;
  onChange: Function;
  includeLabel: boolean;
  inGrid: boolean;
  inputPrefix: string;
  readOnly: boolean;
  flexBox: boolean;
  labelCols: number;
  inputCols: number;
  id: string;
  hint: string;
  value: any;
  validationErrors: Array<any>;
  showWhenNotActive: boolean;
  label: string;
  type: string;
  search: boolean;
  creatable: boolean;
  deletable: boolean;
  rawLabel: boolean; // option to disable label formatting (e.g. for allcaps fields).
  valueOnly: boolean;
  placeholder?: string;
  column: any;
  updateCellState: Function;
  optionRenderer?: FunctionComponent<any>;
  valueKey?: string;
  labelKey?: string;
  'data-testid': string;
  testId?: string; //Use data-testid as priority.
};

/**
 * New version of Dropdown supporting multiple columns of dropdown options by using latest react-select.
 */
const MultiQueryDropdown = forwardRef<any, IMultiQueryDropdown>((props, ref: MutableRefObject<any>) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState(null);
  const filterString = useRef('');
  const lastKeyCode = useRef(null);
  const lastShift = useRef(false);
  const styles = useStyles();
  const dropdownRef = useRef(null);

  const { column, updateCellState } = props;
  const dropdownOptions = column?.options?.dropdown || {};

  const {
    multiQuery,
    selectModal,
    selectModalData,
    selectModalUpdateOpen,
    placeholder,
    optionRenderer: OptionRenderer,
    scanQuery,
    customVariables,
  } = dropdownOptions;

  const getDefaultSearchVariables = useSearchVariables();
  const getSearchVariables = (filterValue?: string) => {
    //Handle predefined filters for individual keys. (E.g. Luckins Items have a filter to exclude items that are already linked).
    const variables = getDefaultSearchVariables(filterValue);
    multiQuery.keys.forEach((key, index) => {
      const config = multiQuery.keys[index];

      if (config.predefinedFilters) {
        variables[`filters${index}`] = [...variables.filters, ...config.predefinedFilters];
      } else {
        variables[`filters${index}`] = variables.filters;
      }
      if (config.disabled) {
        variables[`excludeList${index}`] = true;
      }
    });
    delete variables.filters;

    const customVars = customVariables?.() || {};
    Object.keys(customVars).forEach((key) => {
      variables[key] = customVars[key];
    });

    return variables;
  };

  const { refetch } = useQuery(multiQuery.query, {
    variables: getSearchVariables(),
    skip: true,
  });

  const { refetch: scanRefetch } = useQuery(scanQuery, {
    skip: true,
    fetchPolicy: 'network-only',
  });

  const runScanQuery = (key) => {
    if (!scanQuery || !filterString.current || filterString.current === '') return;

    const scanVariables = {
      filter: {
        filterOperation: 'CONTAINS',
        filterParameters: {
          field: 'generic',
          value: filterString.current,
        },
      },
    };

    const customVars = customVariables?.() || {};
    Object.keys(customVars).forEach((key) => {
      scanVariables[key] = customVars[key];
    });

    scanRefetch(scanVariables).then((response) => {
      if (response?.data?.data) {
        props.onChange(response.data.data, key, { skipTabBehaviour: true, setInactive: true });
      }
    });
  };

  const handleGroupClick = ({ index }) => {
    const config = multiQuery.keys[index];

    setModalTab(config.title === 'Luckins Products' ? 'luckins' : null);
    openExpandedSearchModal();
  };

  const formatGroupLabel = (data) => {
    const additionalInformationPill =
      data.label === 'Luckins Products' ? (
        <div style={{ display: 'inline-block' }}>
          <LuckinsPill />
        </div>
      ) : null;
    return (
      <div className={styles.groupLabel} onClick={() => handleGroupClick(data)}>
        {data.label} <Badge>{`${data.genericCount}`}</Badge>
        {additionalInformationPill}
      </div>
    );
  };

  const formatOptionLabel = (data) => {
    const { value, label, key } = data;

    if (OptionRenderer && key === 'list0') {
      return <OptionRenderer option={value} />;
    }

    //Only want to display the cards in the menu. Does not fit into the control section.
    return <div style={{ display: 'flex', color: '#a0a0a0' }}>{data?.reference || label}</div>;
  };

  const openExpandedSearchModal = (event?: any) => {
    if (event && event.type === 'mousedown' && event.button !== 0) {
      return;
    }
    if (selectModalUpdateOpen) {
      //Update the modal state in the containing form (E.g. Sales Order Form).
      //Used to prevent tabs/clicks/etc accidentally being captured by other components on that form.
      selectModalUpdateOpen(true);
    }
    if (updateCellState) {
      //Update the modal state in the Cell.
      //Used to lock the cell to the Editor, as the Modal is a child of that Editor.
      updateCellState({ modalOpen: true });
    }
    setModalOpen(true);
  };

  //Debounced loadOptions - The 'useCallback' with [] is important - Otherwise it will define a new function each render so will never 'debounce' anything.
  const loadOptions = useCallback(
    debounce((value) => {
      return refetch(getSearchVariables(value)).then((response) => {
        return Object.keys(response.data)
          .map((key, index) => {
            const config = multiQuery.keys[index];
            if (!response.data[key] || config.disabled) return null;
            return {
              key: key,
              index: index,
              label: config.title,
              count: response.data[key].count,
              genericCount: response.data[key].genericCount,
              options: response.data[key].edges.map((item) => {
                const itemNode = item.node;
                return {
                  key: key,
                  label: itemNode[config.labelKey],
                  value: itemNode,
                  menuModule: config.title,
                };
              }),
            };
          })
          .filter((entry) => !!entry);
      });
    }, 500),
    []
  );

  const handleKeyDown = (event) => {
    lastKeyCode.current = event.keyCode;
    lastShift.current = isShiftKeyHeldDown(event);

    switch (event.keyCode) {
      case 9: // tab
        if (
          ref.current.state?.loadedOptions?.length > 0 &&
          ref.current.state.inputValue === ref.current.state.loadedInputValue
        ) {
          return;
        }
        runScanQuery?.(9);
        return;
      case 13: // enter
        //If we don't stop propagation for the enter key, this dropdown fails to select an entry.
        event.stopPropagation();

        if (
          ref.current.state?.loadedOptions?.length > 0 &&
          ref.current.state.inputValue === ref.current.state.loadedInputValue
        ) {
          return;
        }
        runScanQuery?.(13);
        break;
      case 27: // escape
      case 38: // up
      case 40: // down
      case 33: // page up
      case 34: // page down
      case 35: // end key
      case 36: // home key
        event.stopPropagation(); //Stop propagation to prevent grid from capturing incorrectly.
        break;
      default:
    }
  };

  const onChange = (newValue) => {
    let val: any = newValue;
    if (Array.isArray(val) && val.length === 0) val = null;
    if (props.valueOnly) {
      val = val && val[props.valueKey];
    }

    props.onChange(val, lastKeyCode.current || 'click', {
      setInactive: true,
      triggerTemporarySave: props.column?.options?.dropdown?.temporaryOnClick,
      shift: lastShift.current,
    });
  };

  const [spaceBelow, setSpaceBelow] = useState(window.innerHeight);
  const [moreSpaceOnRight, setMoreSpaceOnRight] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (dropdownRef.current) {
        const boundingRect = dropdownRef.current.getBoundingClientRect();
        const newSpaceBelow = window.innerHeight - boundingRect.bottom;
        setSpaceBelow(newSpaceBelow);

        const spaceOnLeft = boundingRect.left;
        const spaceOnRight = window.innerWidth - boundingRect.right;

        setMoreSpaceOnRight(spaceOnRight > spaceOnLeft);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [dropdownRef]);

  if (props.inGrid) {
    return (
      <>
        <InnerSelectModal
          onClick={() => {}}
          selectModal={selectModal}
          selectModalData={selectModalData}
          selectModalUpdateOpen={selectModalUpdateOpen}
          updateCellState={updateCellState}
          selectValue={onChange}
          focusOnInput={() => {}}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          activeTabId={modalTab}
        />
        <div ref={dropdownRef}>
          <Async
            id={props['data-testid'] ?? props.testId}
            ref={ref}
            components={{
              MenuList: MultiMenuList,
              DropdownIndicator: SearchIndicator,
            }}
            isMulti={false}
            loadOptions={loadOptions}
            onChange={(selectedOption: { value: any }) => {
              onChange(selectedOption.value);
            }}
            onInputChange={(value) => {
              filterString.current = value;
            }}
            styles={{
              menuPortal: (provided, state) => {
                let moveRight = props?.column?.width ?? 0;
                moveRight = moreSpaceOnRight ? moveRight : -moveRight;
                return {
                  ...provided,
                  zIndex: 1000000,
                  transform:
                    spaceBelow && MAX_MENU_HEIGHT && spaceBelow < MAX_MENU_HEIGHT
                      ? `translateY(-${MAX_MENU_HEIGHT - spaceBelow}px) translateX(${moveRight}px)`
                      : 'none',
                };
              },
              control: (provided) => ({
                ...provided,
                border: 0,
                boxShadow: 'none',
                minHeight: '30px',
                height: '32px',
              }),
              menu: (provided, state) => {
                const width = state.options.filter((option: any) => option.options.length > 0).length * 550;
                return {
                  ...provided,
                  width: `${width}px`,
                  backgroundColor: p4Tokens.colorNeutralBackground1,
                };
              },
              menuList: (provided) => {
                return {
                  ...provided,
                  maxHeight: MAX_MENU_HEIGHT,
                };
              },
              indicatorsContainer: (provided) => {
                return { ...provided, padding: '0px' };
              },
              dropdownIndicator: (provided) => ({
                ...provided,
                padding: '5px',
              }),
              indicatorSeparator: (provided) => ({
                ...provided,
                width: '1px',
                marginTop: '0px',
                marginBottom: '0px',
              }),
              input: (provided) => ({
                ...provided,
                margin: '0px',
              }),
            }}
            menuPortalTarget={document.body}
            formatGroupLabel={formatGroupLabel}
            formatOptionLabel={formatOptionLabel}
            placeholder={props.value || placeholder}
            onSearchIconClick={openExpandedSearchModal}
            onKeyDown={handleKeyDown}
            value={props.value}
            menuPosition={'fixed'}
          />
        </div>
      </>
    );
  }
  console.warn('MultiDropdown is not yet implemented outside of Grids.');
});

MultiQueryDropdown.defaultProps = {
  active: false,
  mandatory: false,
  clearButton: false,
  placeholder: '',
  onChange: () => {},
  optionRenderer: null,
  includeLabel: true,
  inGrid: false,
  inputPrefix: null,
  readOnly: false,
  flexBox: true,
  labelCols: 1 / 4,
  inputCols: 3 / 4,
  value: '',
  validationErrors: [],
  hint: '',
  showWhenNotActive: false,
  type: 'text',
  label: '',
  id: '',
  search: false,
  creatable: false,
  deletable: false,
  valueKey: 'value',
  labelKey: 'label',
};
MultiQueryDropdown.displayName = 'Dropdown';

export default forwardRef<any, ComponentProps<typeof MultiQueryDropdown>>((props, ref) => {
  const { readOnly } = props;

  if (readOnly) return <ReadOnlyComponent {...(props as unknown as ComponentProps<typeof ReadOnlyComponent>)} />;

  //Not passing ref to MultiDropdown because it's a function component and not handled currently.
  //Don't think it needs to be, should be better ways to implement similar logic.
  return <MultiQueryDropdown ref={ref} {...props} />;
});
